/**
 * useVisibilityReconnect Hook
 *
 * Detects when the browser tab returns from being hidden and triggers
 * reconnection of all real-time data systems: Triton WebSocket,
 * MarketData WebSocket, Supabase Realtime channels, and React Query.
 *
 * Only triggers reconnection if the tab was hidden for >= 5 seconds.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WebSocketService } from '@/services/ws/WebSocketService';
import { MarketDataService } from '@/services/market-data/MarketDataService';
import { addBreadcrumb } from '@/lib/sentry';

export type ReconnectState = 'idle' | 'reconnecting' | 'failed';

const RECONNECT_THRESHOLD_MS = 5_000;
const FORCE_RECONNECT_THRESHOLD_MS = 60_000;
const RECONNECT_TIMEOUT_MS = 10_000;

export function useVisibilityReconnectCore() {
  const queryClient = useQueryClient();
  const hiddenAtRef = useRef<number | null>(null);
  const reconnectingRef = useRef(false);
  const failTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusUnsubRef = useRef<(() => void) | null>(null);

  const [reconnectState, setReconnectState] = useState<ReconnectState>('idle');
  const [wakeCounter, setWakeCounter] = useState(0);

  const cleanup = useCallback(() => {
    if (failTimeoutRef.current) {
      clearTimeout(failTimeoutRef.current);
      failTimeoutRef.current = null;
    }
    if (statusUnsubRef.current) {
      statusUnsubRef.current();
      statusUnsubRef.current = null;
    }
    reconnectingRef.current = false;
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab going hidden
        hiddenAtRef.current = Date.now();
        addBreadcrumb('tab.hidden', 'navigation', {
          timestamp: Date.now(),
          path: window.location.pathname,
        });
        return;
      }

      // Tab becoming visible
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;

      if (!hiddenAt) return;

      const hiddenDuration = Date.now() - hiddenAt;

      // Short background: no reconnection needed
      if (hiddenDuration < RECONNECT_THRESHOLD_MS) {
        addBreadcrumb('tab.visible', 'navigation', {
          hiddenDuration,
          reconnectTriggered: false,
        });
        return;
      }

      // Prevent re-entrancy
      if (reconnectingRef.current) return;
      reconnectingRef.current = true;

      const ws = WebSocketService.getInstance();
      const md = MarketDataService.getInstance();

      addBreadcrumb('tab.visible.reconnect', 'navigation', {
        hiddenDuration,
        wsStatus: ws.status,
        wsLastMessage: ws.lastMessageAt,
        wsIsStale: ws.isStale(FORCE_RECONNECT_THRESHOLD_MS),
      });

      setReconnectState('reconnecting');
      const reconnectStart = Date.now();

      // Phase A: Force reconnect WebSockets if stale or long hidden
      const shouldForceWs =
        ws.status === 'disconnected' ||
        ws.status === 'error' ||
        ws.isStale(FORCE_RECONNECT_THRESHOLD_MS) ||
        hiddenDuration > FORCE_RECONNECT_THRESHOLD_MS;

      if (shouldForceWs) {
        ws.forceReconnect();
      }

      // MarketData uses the same conditions
      md.forceReconnect();

      // Invalidate active React Query queries
      queryClient.invalidateQueries({ refetchType: 'active' });

      // Increment wakeCounter to trigger Supabase channel re-subscription
      setWakeCounter((c) => c + 1);

      // Phase B: Monitor reconnection
      statusUnsubRef.current = ws.onStatusChange((status) => {
        if (status === 'connected') {
          addBreadcrumb('tab.reconnect.success', 'navigation', {
            duration: Date.now() - reconnectStart,
          });
          setReconnectState('idle');
          cleanup();
        }
      });

      // If already connected (e.g. short background with fresh WS), resolve immediately
      if (ws.connected && !shouldForceWs) {
        setReconnectState('idle');
        cleanup();
        return;
      }

      // Timeout: if not reconnected within 10s, mark as failed
      failTimeoutRef.current = setTimeout(() => {
        addBreadcrumb('tab.reconnect.failed', 'navigation', {
          duration: Date.now() - reconnectStart,
          wsStatus: ws.status,
        });
        setReconnectState('failed');
        cleanup();
      }, RECONNECT_TIMEOUT_MS);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      cleanup();
    };
  }, [queryClient, cleanup]);

  return { reconnectState, wakeCounter };
}
